<template>
  <div class="destination">
    <a
      :href="destinationHref"
      class="image-link"
    >
      <v-lazy-image
        :src="destinationImg"
        :alt="destinationTitle"
        @intersect="destinationIntersected"
      />
    </a>

    <div class="destination-content">
      <div class="info-cover">
        <div
          v-if="destinationLabels.length"
          class="label-wrapper"
        >
          <div class="label-cover">
            <span
              v-for="label in destinationLabels"
              :key="label"
              class="label"
            >
              {{ getLabelTranslated(label) }}
            </span>
          </div>
        </div>

        <div class="bottom">
          <div class="destination-info">
            <a
              v-dompurify-html="destinationTitle"
              :href="destinationHref"
              class="destination-title"
            />
            <span class="destination-airport">{{ destination.apt }}, {{ destination.country }}</span>
          </div>
          <a
            class="more-link"
            :href="destinationHref"
          >
            {{ $t('destinationsSilo.more') }}
          </a>
        </div>
      </div>

      <template v-if="loaderVisible">
        <loader-gif class="loader-gif" />
      </template>

      <template v-else>
        <div
          v-if="moreLinkVisible"
          class="wrap button"
        >
          <white-link
            :href="getHref(origin, code)"
            :label="destinationLabel"
          />
        </div>

        <template v-if="showPrices && !hasRip">
          <histogram
            :prices-from-parent="prices"
            :destination-code="code"
            :origin-code="origin"
            :selected-label="selectedLabel"
          />
        </template>
      </template>
    </div>

    <div class="separator" />
  </div>
</template>

<script>
  import VLazyImage from 'v-lazy-image'
  import WhiteLink from 'components/StyledLinks/White'
  import LoaderGif from 'components/LoaderGif'
  import Histogram from 'components/Histogram/Histogram'
  import HistogramService from 'services/AirBaltic/Destinations/Histogram'
  import { mapState, mapGetters } from 'vuex'
  import conf from 'conf'
  import isEmpty from 'lodash-es/isEmpty'

  export default {
    components: {
      VLazyImage,
      WhiteLink,
      Histogram,
      LoaderGif
    },

    props: {
      destination: {
        type: Object,
        required: true
      },

      origin: {
        type: String,
        required: true
      },

      originType: {
        type: String,
        required: true
      },

      labels: {
        type: Array,
        default: () => [],
        required: false
      }
    },

    data () {
      return {
        loaderVisible: false,
        prices: []
      }
    },

    computed: {
      ...mapGetters('device', ['isMobile', 'notMobile']),
      ...mapState('destinationsSilo', ['allLabels', 'selectedLabel']),

      ...mapState('indexTabs', ['ripTabs']),

      destinationImg () {
        return `/destinations-silo-2019/${this.code.toLowerCase()}.jpg?${
          conf.assetsVersion
        }`
      },

      destinationTitle () {
        return this.destination.city + ' (' + this.code + ')'
      },

      destinationLabels () {
        const necessaryLabels = ['label_direct_flights', 'label_new']

        return this.labels.filter((label) => necessaryLabels.indexOf(label) >= 0)
      },

      destinationHref () {
        return this.origin
          ? this.destination.href + '?origin=' + this.origin + this.originType
          : this.destination.href
      },

      code () {
        return this.destination.code.slice(0, 3).toUpperCase()
      },

      destinationLabel () {
        if (this.isMobile) {
          return this.$t('destinationsSilo.more')
        }

        return this.$t('destinationsSilo.checkPrices')
      },

      showPrices () {
        return !isEmpty(this.prices) && !this.isAllPricesNull
      },

      isAllPricesNull () {
        return !this.prices.find((item) => item.price !== null)
      },

      moreLinkVisible () {
        if (this.notMobile) {
          return !this.showPrices && !this.hasRip
        }

        return true
      },
      hasRip () {
        return this.ripTabs.includes('FLT')
      }
    },
    methods: {
      destinationIntersected () {
        if (!this.destination.fastSearch || this.hasRip) {
          return
        }

        this.loaderVisible = true

        new HistogramService()
        .getHistogramData(this.origin, this.code)
        .then((response) => {
          this.prices = response
        })
        .catch(() => {
          return false
        })
        .finally(() => {
          this.loaderVisible = false
        })
      },

      getLabelTranslated (key) {
        const translationKey = `destinationLabels.${key.toLowerCase()}`
        if (
          this.$t(translationKey) !== '' &&
          this.$t(translationKey) !== translationKey
        ) {
          return this.$t(translationKey)
        }
        return this.allLabels[`${key}`]
      },

      getHref (origCode, destCode) {
        if (this.isMobile) {
          return this.destinationHref
        }

        return (
          conf.newIbeUrl +
          '/' +
          this.$i18n.locale() +
          '/fb/histogram?originCode=' +
          origCode +
          '&destinCode=' +
          destCode +
          '&numAdt=1&tripType=oneway'
        )
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import '~assets/styles/common.scss';

  .destination {
    position: relative;
    min-height: 280px;
    background: white;
    margin-bottom: 24px;
    border-radius: 6px;

    @include bp-mf-tablet {
      border-radius: 0;
      min-height: auto;
      display: flex;
      flex-flow: row wrap;
      align-items: flex-start;
      padding: 28px 0 0 28px;
      border-bottom: none;
      margin-bottom: 0;

      &:last-of-type {
        .separator {
          background-color: white;
        }
      }
    }

    .image-link {
      display: flex;
    }

    &-info {
      display: flex;
      flex-direction: column;
    }

    &-title {
      color: $blue-dark;
      font-size: rem(20, 16);
      font-weight: 500;
      line-height: rem(21, 16);
      text-decoration: none;

      @include bp-mf-tablet {
        font-size: rem(20, 16);
      }
    }

    &-airport {
      color: $blue-light;
      font-size: rem(12, 16);
      line-height: rem(18, 16);
      margin-top: 8px;
    }
  }

  .wrap {
    .button {
      margin: 0 16px;

      a {
        margin-top: 13px;
        height: 36px;
        border-radius: 4px;
        box-shadow: none;
      }
    }

    &:last-of-type {
      padding-bottom: 20px;

      &.button {
        padding: 19px 16px;

        @include bp-mf-tablet {
          width: 100%;
          padding: 19px 0 0 0;
        }
      }

      @include bp-mf-tablet {
        padding-bottom: 0;
        margin-left: 0;
      }
    }
  }

  .separator {
    display: none;

    @include bp-mf-tablet {
      display: block;
      height: 1px;
      width: 100%;
      background-color: #e2e2e2;
      margin-top: 28px;
    }
  }

  .more-link {
    display: none;

    @include bp-mf-tablet {
      display: block;
      color: #313892;
      font-size: em(12);
      font-weight: 500;
      line-height: 14px;
      text-align: right;
      margin: 5px 31px 0 auto;
    }
  }

  .destination-content {
    @include bp-mf-tablet {
      display: flex;
      flex-flow: row wrap;
      width: calc(100% - 163px);
      padding-left: 28px;
    }

    .btn-white {
      @include bp-mf-tablet {
        max-width: 238px;
        padding: 0;
      }
    }

    .loader-gif {
      position: absolute;
      transform: translate(-50%, -50%);
      left: 50%;
      top: 60%;
    }

    .label-wrapper {
      left: 16px;
      position: absolute;
      top: 24px;

      @include bp-mf-tablet {
        margin-bottom: 8px;
        position: static;
      }
    }

    .label-cover {
      display: flex;
      align-items: center;
      margin: -5px;
      flex-wrap: wrap;

      .label {
        border-radius: 10.5px;
        font-size: 12px;
        line-height: 14px;
        font-weight: 500;
        color: #737a1d;
        background-color: #f4f7d0;
        padding: 3px 12px;
        margin: 5px;

        @include bp-mf-tablet {
          padding: 3px 6px;
          border-radius: 4px;
        }
      }
    }

    .info-cover {
      display: flex;
      flex-direction: column;
      width: 100%;

      @include bp-mobile {
        padding: 0 16px;
      }

      .bottom {
        display: flex;
        flex-direction: column;
        padding-top: 2px;

        @include bp-mf-tablet {
          flex-direction: row;
          align-items: center;
        }
      }
    }
  }

  .histogram {
    padding: 12px 16px 8px;

    @include bp-mf-tablet {
      padding: 18px 0 0;
      width: 100%;
    }

    &:before,
    &:after {
      @include bp-mf-tablet {
        content: '';
        width: 100%;
        order: 1;
      }
    }

    ::v-deep .histogram-content {
      @include bp-mf-tablet {
        overflow: auto;
      }

      @include bp-mf-desktop-large() {
        overflow: visible;
      }

      .balloon.desktop:hover {
        display: none !important;

        @include bp-mf-desktop-large() {
          display: block !important;
        }
      }
    }

    ::v-deep .histogram-wrap {
      @include bp-mf-tablet {
        padding-right: 9px;

        .column {
          width: 42px;
        }
      }
    }
  }

  .v-lazy-image {
    width: 100%;
    height: auto;
    padding-bottom: 16px;
    border-radius: 6px 6px 0 0;
    cursor: pointer;

    @include bp-mf-tablet {
      height: 120px;
      width: 163px;
      padding: 0;
      border-radius: 0;
    }
  }
</style>
